<script setup lang="ts">
import DJIcon from '~/components/ui/icon/Icon.vue'

defineOptions({
  name: 'DJButton',
})
defineProps({
  /**
   * @description loading the button
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * @description button size
   */
  size: {
    type: String,
    values: buttonSizes,
    default: '',
  },
  /**
   * @description disable the button
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * @description button type
   */
  type: {
    type: String,
    values: buttonTypes,
    default: '',
  },
  /**
   * @description determine whether it's a round button
   */
  rounded: {
    type: Boolean,
    default: false,
  },
  /**
   * @description icon component
   */
  icon: {
    type: Object,
    default: null,
  },
  /**
   * @description prefix icon component
   */
  prefixIcon: {
    type: Object,
    default: null,
  },
  /**
   * @description suffix icon component
   */
  suffixIcon: {
    type: Object,
    default: null,
  },
  /**
   * @description native button type
   */
  nativeType: {
    type: String,
    values: buttonNativeTypes,
    default: 'button',
  },
})

function mapDisable(disable: boolean) {
  return disable ? 'is-disabled' : ''
}
function mapRounded(round: boolean) {
  return round ? 'is-rounded' : ''
}
function mapClass(property: string) {
  return property ? `dj-button--${property}` : ''
}
function mapSizeIcon(size: string) {
  switch (size) {
    case 'large':
      return 18
    case 'default':
      return 16
    case 'small':
      return 14
  }
  return 16
}
</script>
<script lang="ts">
export const buttonTypes = ['accent', 'primary', 'secondary', 'tertiary', 'glass', 'destructive', ''] as const
export const buttonSizes = ['', 'small', 'default', 'large', ''] as const
export const buttonSizeMap = {
  large: 56,
  default: 48,
  small: 40,
} as const
export const buttonNativeTypes = ['button', 'submit', 'reset'] as const
</script>

<template>
  <button class="dj-button" :disabled="disabled" :class="[mapDisable(disabled), mapClass(type), mapClass(size), mapRounded(rounded)]" v-bind="$attrs">
    <template v-if="!loading">
      <template v-if="icon">
        <DJIcon :size="mapSizeIcon(size)">
          <component :is="icon" />
        </DJIcon>
      </template>
      <template v-else>
        <template v-if="prefixIcon">
          <DJIcon class="mr-8" :size="mapSizeIcon(size)">
            <component :is="prefixIcon" />
          </DJIcon>
        </template>
        <slot></slot>
        <template v-if="suffixIcon">
          <DJIcon class="ml-8" :size="mapSizeIcon(size)">
            <component :is="suffixIcon" />
          </DJIcon>
        </template>
      </template>
    </template>
    <span v-else class="spinner-outer"><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>
  </button>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/src/_spinner.scss';
.dj-button {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
  text-align: center;
  box-sizing: border-box;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  vertical-align: middle;
  padding: 0 28px; // v2 Default button padding (Buttons L248–250; was 8px 10px)
  font-size: 15px; // v2 Default label 15px (Buttons L248–250; was 16)
  font-family: $font-family-base; // Work Sans (§8)
  font-weight: 600; // v2 button label 600 (Buttons L242–250; was 500 — a pre-v2 carryover)
  letter-spacing: -0.01em; // v2 button label tracking (Buttons L242–250)
  line-height: 24px;
  border: 1px solid transparent;
  border-radius: $radius-xl; // v2 12px (Buttons L127; was $radius-lg/8px)
  height: 48px; // v2 default size 48h (Buttons L160–165; was 40)
  transition-property: background-color, border-color, color, transform, box-shadow;
  transition-duration: $dur-base; // §11
  transition-timing-function: $ease-out;

  // Default role = green primary (§9). accent/primary reinforce it; spinner
  // colour follows `currentColor`, so it is white here, green on outline/ghost.
  // (Declarations kept above @include focus-ring — the mixin emits nested
  // rules, and Sass warns on declarations that follow nested rules.)
  color: $white; // v2 filled-button label is white (Buttons L27–29, L540/L543)
  background-color: $brand-green;
  box-shadow: $shadow-sm;
  @include focus-ring; // §14 — v2 GREEN 2px outline on :focus-visible

  &:hover:not(.is-disabled) {
    color: $white;
    background-color: $green-deep; // v2 Green Dark #0F5344 (Buttons L10–11)
    transform: translateY(-1px);
    box-shadow: $shadow-md;
  }
  &:active:not(.is-disabled) {
    background-color: $green-pressed;
    transform: translateY(0);
    box-shadow: $shadow-sm;
  }

  &.is-rounded {
    border-radius: $radius-pill;
  }

  &.is-disabled,
  &.is-disabled:hover,
  &.is-disabled:focus {
    cursor: not-allowed;
    pointer-events: none;
    opacity: $disabled-opacity; // v2 Rules L538 — disabled = opacity 0.5 only, never recolour
    transform: none;
    box-shadow: $shadow-sm;
  }

  // primary === green conversion CTA. May REPEAT across a view (v2 L535–536).
  &--primary {
    color: $white;
    background-color: $brand-green;

    &:hover:not(.is-disabled) {
      color: $white;
      background-color: $green-deep;
    }
    &:active:not(.is-disabled) {
      background-color: $green-pressed;
    }
  }

  // accent === gold CTA fill (v2 L12–15 "Accent / CTA fill"; L128–135 state
  // gallery; L539–541 white label on gold). Use ONLY ONE Accent / CTA per view
  // (v2 L535–536); green Primary may repeat. Per-view audit lands in Wave 6
  // (V27). Click returns toward default (v2 L127) — keep the base translateY(0)
  // tactile cue; reuse $gold-dark for the pressed instant (no invented hex).
  // Focus ring is the base GREEN ring (inherited).
  &--accent {
    color: $white;
    background-color: $gold-accent;

    &:hover:not(.is-disabled) {
      color: $white;
      background-color: $gold-dark; // v2 Gold Dark #A8830F
    }
    &:active:not(.is-disabled) {
      background-color: $gold-dark;
    }
  }

  // secondary === green outline; FILLS solid green on hover with a white label (v2 Buttons — Secondary)
  &--secondary {
    color: $brand-green;
    background-color: transparent;
    border-color: $brand-green;
    border-width: 2px; // v2 Secondary — 2px green outline
    box-shadow: none;

    &:hover:not(.is-disabled) {
      color: $white;
      background-color: $brand-green; // v2 hover = SOLID green fill (was green @ 8% tint)
      border-color: $brand-green;
      transform: none;
      box-shadow: none;
    }
    &:active:not(.is-disabled) {
      color: $white;
      background-color: $green-deep; // pressed darken, white label
      border-color: $green-deep;
      box-shadow: none;
    }
    &.is-disabled {
      // v2 disabled = keep the green outline at 50% (base .is-disabled supplies opacity); was greyed to hairline/muted
      color: $brand-green;
      background-color: transparent;
      border-color: $brand-green;
    }
  }

  // tertiary === low-emphasis green ghost (v2 guideline Buttons — Tertiary): green label,
  // transparent fill, green-tint hover with a Green-Dark label. The border is kept visible (warm
  // hairline) by request; the guideline's own tertiary uses a transparent border.
  &--tertiary {
    color: $brand-green;
    background-color: transparent;
    border-color: $border-hairline;
    box-shadow: none;

    &:hover:not(.is-disabled) {
      color: $green-deep; // Green Dark #0F5344
      background-color: rgba($brand-green, 0.1); // guideline hover tint
      border-color: $border-hairline;
      transform: none;
      box-shadow: none;
    }
    &:active:not(.is-disabled) {
      color: $green-deep;
      background-color: rgba($brand-green, 0.1);
      box-shadow: none;
    }
  }

  // destructive === #EF4444 fill / white label; reuses the base state curve
  // (v2 L152–159 gallery; L545). Replaces the retired --warning yellow stub
  // (yellow fills violate §7). Shares the base disabled rule (opacity 0.5).
  &--destructive {
    color: $white;
    background-color: $border-error;

    &:hover:not(.is-disabled) {
      color: $white;
      background-color: $destructive-hover; // v2 Destructive hover = hsl(0 84% 52%) = #EB1E1E (was $border-error-dark #C0392B, the error-BORDER red)
    }
    &:active:not(.is-disabled) {
      background-color: $destructive-hover;
    }
  }

  // glass: translucent button over photography (§13). Keep the blur treatment.
  &--glass {
    color: $cream;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: none;

    &:hover:not(.is-disabled) {
      color: $cream;
      background: rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(10px);
      transform: none;
      box-shadow: none;
    }
  }

  &--large {
    height: 56px; // v2 Large 56h (Buttons L160–165; was 44)
    padding: 0 32px; // v2 Large padding (Buttons L250)
    font-size: 16px; // v2 Large label 16px (Buttons L250)
  }

  &--small {
    height: 40px; // v2 Small 40h (Buttons L160–165; was 36)
    padding: 0 20px; // v2 Small padding (Buttons L248)
    font-size: 14px; // v2 Small label 14px (Buttons L248)
    line-height: 20px;
  }

  // ── v2 dark mode (Wave 7/V30a) ───────────────────────────────────────────────
  // On the dark canvas, FILLED green/gold labels flip to black $ink #0A0A0A (Rules
  // §1082/§1085 + Dark-Mode-Preview §82); brand fills carry over unchanged. Tertiary/ghost
  // + secondary-at-rest text LIFTS to mint — the pinned $brand-green/$green-deep fail WCAG
  // on #080808 (§249-252, G5). Destructive keeps white on red; glass keeps cream over
  // photography. Only labels (+ the tertiary hover tint / secondary outline) change here —
  // fills are brand tokens that resolve the same in both themes.
  [data-theme='dark'] & {
    color: $ink; // base filled green (type='') → black label
    &:hover:not(.is-disabled),
    &:active:not(.is-disabled) {
      color: $ink;
    }
    &.dj-button--primary,
    &.dj-button--accent {
      color: $ink; // filled green/gold → black label
      &:hover:not(.is-disabled),
      &:active:not(.is-disabled) {
        color: $ink;
      }
    }
    // secondary — mint outline + label at rest/disabled (green text fails WCAG on dark, G5);
    // the green-FILL hover/active states take the black label (base hover keeps the green fill).
    &.dj-button--secondary {
      color: $mint-dark;
      border-color: $mint-dark;
      &.is-disabled {
        color: $mint-dark;
        border-color: $mint-dark;
      }
      &:hover:not(.is-disabled),
      &:active:not(.is-disabled) {
        color: $ink;
      }
    }
    // tertiary / ghost — lift to mint (§249-252, G5)
    &.dj-button--tertiary {
      color: $mint-dark;
      &.is-disabled {
        color: $mint-dark;
      }
      &:hover:not(.is-disabled),
      &:active:not(.is-disabled) {
        color: $mint-dark-strong;
        background-color: rgba($mint-dark, 0.16);
      }
    }
    // destructive keeps its white label on red; glass keeps cream over photography
    &.dj-button--destructive,
    &.dj-button--destructive:hover:not(.is-disabled),
    &.dj-button--destructive:active:not(.is-disabled) {
      color: $white;
    }
    &.dj-button--glass,
    &.dj-button--glass:hover:not(.is-disabled) {
      color: $cream;
    }
  }

  // Mobile (§9): hover collapses to tap feedback.
  @include phone {
    &:active:not(.is-disabled) {
      transform: scale(0.98);
    }
  }
}
</style>
